// Student Overall Performance Report — one consolidated dashboard per student.
//
// Deliberately NOT a single-exam report card (that is
// home/examReportsAnalytics/studentReportCard.js). Every figure is cumulative across the
// student's COMPLETE examination history; the exam table at the foot exists only so each
// "Exam 1..N" point on the charts can be identified. The caller passes no stream filter, so
// a student spanning several exam streams still gets one record.
//
// Layout follows the approved dashboard design: letterhead band · student card with grade
// and performance gauge · KPI strip · score trend + subject-wise · question / time / rank ·
// topic-wise + summary + insights · exam table · prediction footer.
//
// Rendering matches the existing report card: real HTML + inline SVG printed through a
// hidden iframe, never an html2canvas raster (which blurs A4 text and cannot paginate).
// Charts are hand-rolled SVG on purpose — no chart library is available inside the print
// frame, and SVG stays sharp at print resolution. Icons are unicode glyphs for the same
// reason: FontAwesome's stylesheet does not exist in the iframe.

import gql from "graphql-tag";
import moment from "moment";
import * as Cookies from "es-cookie";
import {
    esc,
    fmtMarks,
    normalizeLogoSrc,
    cookieInstitutionName,
} from "../home/examReportsAnalytics/studentReportCard";

/* ------------------------------------------------------------------ queries */

// Every field here is already used by a sibling profile tab against this same backend
// (OverviewTab, PerformanceTab, TimeAnalysisTab), so the selection set is known-good.
// exam_type 0 = all exam types.
const GET_STUDENT_REPORT = gql`
  query (
    $mobile: String
    $student_user_id: Int
    $edu_student_id: Int
    $exam_type: Int
    $exam_stream: Int
    $fromDate: String
    $toDate: String
    $target: String
  ) {
    getStudentPerformanceReport(
      mobile: $mobile
      student_user_id: $student_user_id
      edu_student_id: $edu_student_id
      exam_type: $exam_type
      exam_stream: $exam_stream
      fromDate: $fromDate
      toDate: $toDate
    ) {
      snapshot(target: $target) {
        attendance
        best_subject
        weak_subject
        target
        predicted_score
        max_score
        predicted_label
        simulated_air
        risk_level
      }
      metrics {
        avg_time_per_question
        attempt_rate
        consistency
        has_time_data
        breakdown {
          total_time_seconds
          total_questions_timed
          consistency_mean
          consistency_std
          consistency_cv
          rank_latest
          rank_baseline
          exams_considered
        }
      }
      examWiseResult {
        id
        exam_name
        date
        marks
        accuracy
        exam_rank
        section_rank
        section_total
        branch_rank
        mode
        correct
        wrong
        skipped
        not_answered
        total_questions
        negative_marks
        total_marks
        over_time
        less_time
      }
      subjectPerformance {
        subject_id
        subject
        best_score
        lowest_score
        section_avg
        branch_avg
        accuracy
      }
      subjectMarksTrend {
        subject
        subject_id
        points {
          exam_name
          marks
          branch_avg
          subject_max
        }
      }
      speedVsAccuracy {
        subject
        subject_id
        accuracy
        attempted
        avg_time_per_question
        timed_questions
        total_questions
        has_time_data
      }
      topicPerformance {
        subject_id
        subject
        chapter
        topic
        attempted
        accuracy_percentage
      }
      examSubjectMatrix {
        exam_paper_id
        session_id
        exam_name
        date
        subjects {
          subject_id
          subject
          attempted
          accuracy
          score
        }
      }
    }
  }
`;

const GET_OVERALL_ATTENDANCE = gql`
  query ($student_mobile: String) {
    getStudentOverallAttendance(student_mobile: $student_mobile) {
      total_present
      total_absent
      total_working_days
      total_marked_days
      percentage
    }
  }
`;

// Supplies the denominator of "Exams Attempted N / M".
const GET_STUDENT_EXAM_STREAMS = gql`
  query ($student_user_id: Int, $edu_student_id: Int) {
    getStudentExamStreams(student_user_id: $student_user_id, edu_student_id: $edu_student_id) {
      stream
      label
      total_exams
      completed
    }
  }
`;

const GET_ORGANISATION_PROFILE = gql`
  query ($institution_id: Int!) {
    getOrganisationProfile(institution_id: $institution_id) {
      id
      organisation_name
      logo
      complete_address
    }
  }
`;

/* ------------------------------------------------------------------ helpers */

const num = (v) => {
    const n = parseFloat(v);
    return isNaN(n) ? 0 : n;
};
const isNum = (v) => v !== null && v !== undefined && v !== "" && !isNaN(parseFloat(v));
const r1 = (n) => Math.round(n * 10) / 10;
const r2 = (n) => Math.round(n * 100) / 100;
const mean = (list) => (list.length ? list.reduce((s, x) => s + x, 0) / list.length : null);
const total = (list) => list.reduce((s, x) => s + x, 0);
// "—" for anything the backend didn't send, so a blank never reads as a zero.
const val = (v) => (v === null || v === undefined || v === "" ? "—" : String(v));
const pctText = (v) => (isNum(v) ? `${r1(num(v))}%` : "—");
const intText = (v) => (isNum(v) ? String(Math.round(num(v))) : "—");
const grouped = (v) => (isNum(v) ? Math.round(num(v)).toLocaleString("en-IN") : "—");
const titleCase = (s) => {
    const str = `${s == null ? "" : s}`.trim();
    return str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : "";
};
const norm = (v) => `${v == null ? "" : v}`.trim().toLowerCase();

// Minutes / seconds → "2h 42m" (or "48m" under the hour).
const fmtDuration = (minutes) => {
    if (!isNum(minutes) || num(minutes) <= 0) return "—";
    const mins = Math.round(num(minutes));
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return h ? `${h}h ${String(m).padStart(2, "0")}m` : `${m}m`;
};

const EXAM_DATE_FORMATS = ["DD-MM-YYYY", "DD/MM/YYYY", "YYYY-MM-DD", "MM/DD/YYYY", moment.ISO_8601];

// Exam dates arrive in several shapes across exam types (day-first strings, ISO, and epochs
// on offline papers) — normalise all of them, and print the raw value rather than "—" when
// it is something we don't recognise, so no information is silently dropped.
const parseExamMoment = (d) => {
    if (!d) return null;
    const raw = `${d}`.trim();
    if (/^\d{10,13}$/.test(raw)) {
        const n = parseInt(raw, 10);
        const m = moment(n < 1e12 ? n * 1000 : n);
        return m.isValid() ? m : null;
    }
    const m = moment(raw, EXAM_DATE_FORMATS, true);
    return m.isValid() ? m : null;
};
export const fmtExamDate = (d) => {
    const m = parseExamMoment(d);
    if (m) return m.format("DD MMM YYYY");
    return d ? String(d) : "—";
};
const examSortKey = (e) => {
    const m = parseExamMoment(e && e.date);
    return m ? m.valueOf() : 0;
};

const studentName = (student) => (student && (student.student_name || student.name)) || "Student";

const resolveMobile = (student) =>
    (student &&
        (student.contact_no || student.student_mobile || student.mobile ||
            student.parent_mobile || student.phone || student.mobile_no)) || "";

// Prediction curve target — same rule as OverviewTab (exam_id is authoritative:
// 1 = NEET, 2 = JEE), falling back to profile text only when it is absent.
const resolveTarget = (student) => {
    const examId = num(student && student.exam_id);
    if (examId === 1) return "neet";
    if (examId === 2) return "jee";
    const text = `${(student && (student.exam_name || student.stream_name)) || ""}`.toLowerCase();
    if (text.includes("neet") || text.includes("bipc") || text.includes("medical")) return "neet";
    return "jee";
};

// Score-% of one exam row (null when the paper's total isn't known).
const examPct = (e) =>
    isNum(e && e.marks) && num(e.total_marks) > 0 ? r1((num(e.marks) / num(e.total_marks)) * 100) : null;

// Later half vs earlier half of a date-ordered series. `betterIsLower` flips the verdict for
// ranks, where a smaller number is the better result.
const halvesTrend = (series, { betterIsLower = false, band = 1.5, round = r1 } = {}) => {
    if (!series || series.length < 2) return null;
    const half = Math.floor(series.length / 2);
    const earlier = mean(series.slice(0, half));
    const later = mean(series.slice(half));
    if (earlier === null || later === null) return null;
    const raw = betterIsLower ? earlier - later : later - earlier;
    const delta = round(raw);
    return {
        delta,
        from: round(earlier),
        to: round(later),
        label: delta > band ? "Improving" : delta < -band ? "Declining" : "Stable",
    };
};

// Letter grade off the cumulative percentage — the band shown on the student card.
const gradeFor = (pct) => {
    if (pct === null) return null;
    if (pct >= 85) return "A+";
    if (pct >= 75) return "A";
    if (pct >= 65) return "B+";
    if (pct >= 55) return "B";
    if (pct >= 45) return "C";
    if (pct >= 35) return "D";
    return "E";
};
const levelFor = (pct) =>
    pct === null ? null : pct >= 75 ? "Excellent" : pct >= 60 ? "Good" : pct >= 45 ? "Average" : "Needs Improvement";

/* ------------------------------------------------------------------- fetch */

// One call for the whole document. Each query is individually tolerant: a missing letterhead
// or an attendance hiccup must not stop the report from printing.
export const fetchStudentReport = async (client, student, examStream) => {
    const mobile = `${resolveMobile(student)}`;
    const studentUserId = parseInt(student && student.student_user_id) || 0;
    const eduStudentId = parseInt(student && student.edu_student_id) || 0;
    // A deliberately wide window ("every exam on record") — this report is the complete
    // history, not the profile page's rolling last-12-months view.
    const toDate = moment().endOf("day");
    const fromDate = moment().subtract(10, "years").startOf("day");

    const safe = (p, label) =>
        p.catch((e) => {
            console.log(label, e);
            return null;
        });

    const perfP = safe(
        client
            .query({
                query: GET_STUDENT_REPORT,
                variables: {
                    mobile,
                    student_user_id: studentUserId,
                    edu_student_id: eduStudentId,
                    exam_type: 0,
                    exam_stream: examStream != null ? Number(examStream) : null,
                    fromDate: String(fromDate.unix()),
                    toDate: String(toDate.unix()),
                    target: resolveTarget(student),
                },
                fetchPolicy: "network-only",
            })
            .then((res) => (res && res.data && res.data.getStudentPerformanceReport) || null),
        "report"
    );

    const attP = mobile
        ? safe(
            client
                .query({
                    query: GET_OVERALL_ATTENDANCE,
                    variables: { student_mobile: mobile },
                    fetchPolicy: "network-only",
                })
                .then((res) => (res && res.data && res.data.getStudentOverallAttendance) || null),
            "attendance"
        )
        : Promise.resolve(null);

    const streamsP = safe(
        client
            .query({
                query: GET_STUDENT_EXAM_STREAMS,
                variables: { student_user_id: studentUserId, edu_student_id: eduStudentId },
                fetchPolicy: "cache-first",
            })
            .then((res) => (res && res.data && res.data.getStudentExamStreams) || []),
        "streams"
    );

    const institutionId = parseInt(Cookies.get("institutionid") || Cookies.get("institution_id")) || 0;
    const orgP = institutionId
        ? safe(
            client
                .query({
                    query: GET_ORGANISATION_PROFILE,
                    variables: { institution_id: institutionId },
                    fetchPolicy: "cache-first",
                })
                .then((res) => (res && res.data && res.data.getOrganisationProfile) || null),
            "org"
        )
        : Promise.resolve(null);

    const [perf, attendance, streams, org] = await Promise.all([perfP, attP, streamsP, orgP]);

    return {
        snapshot: (perf && perf.snapshot) || null,
        metrics: (perf && perf.metrics) || null,
        examRows: [...((perf && perf.examWiseResult) || [])].sort((a, b) => examSortKey(a) - examSortKey(b)),
        subjectRows: (perf && perf.subjectPerformance) || [],
        subjectTrend: (perf && perf.subjectMarksTrend) || [],
        speed: (perf && perf.speedVsAccuracy) || [],
        topics: (perf && perf.topicPerformance) || [],
        matrix: [...((perf && perf.examSubjectMatrix) || [])].sort((a, b) => examSortKey(a) - examSortKey(b)),
        attendance: attendance || null,
        streams: streams || [],
        org: org || null,
        loaded: !!perf,
    };
};

/* --------------------------------------------------------------- summarising */

// Per-subject series from the exam × subject matrix, in exam date order — what makes a
// per-subject average and improvement trend possible (subjectPerformance carries only
// best / lowest / accuracy).
const subjectSeriesFrom = (matrix = []) => {
    const series = new Map();
    matrix.forEach((m) => {
        (m.subjects || []).forEach((s) => {
            const key = norm(s.subject) || `id-${s.subject_id}`;
            if (!series.has(key)) series.set(key, { subject: s.subject, points: [] });
            series.get(key).points.push({
                score: isNum(s.score) ? num(s.score) : null,
                accuracy: isNum(s.accuracy) ? num(s.accuracy) : null,
                attempted: num(s.attempted),
            });
        });
    });
    return series;
};

// Everything the dashboard prints, derived once. Figures come from the exam rows the report
// itself lists, so no panel can disagree with another; the snapshot supplies only what no
// table carries (prediction, risk), and `metrics` the backend's own time / consistency work.
export const summariseReport = ({
    examRows = [],
    subjectRows = [],
    subjectTrend = [],
    speed = [],
    topics = [],
    matrix = [],
    metrics,
    attendance,
    streams = [],
    snapshot,
}) => {
    const marksList = examRows.filter((e) => isNum(e.marks)).map((e) => num(e.marks));
    const accList = examRows.filter((e) => isNum(e.accuracy)).map((e) => num(e.accuracy));
    const rankList = examRows.filter((e) => num(e.exam_rank) > 0).map((e) => num(e.exam_rank));
    const pctList = examRows.map(examPct).filter((p) => p !== null);
    const sumOf = (k) => total(examRows.map((e) => num(e[k])));

    const scored = total(marksList);
    const maxMarks = total(examRows.filter((e) => isNum(e.total_marks)).map((e) => num(e.total_marks)));
    const questions = sumOf("total_questions");
    const correct = sumOf("correct");
    const wrong = sumOf("wrong");
    const attempted = Math.max(0, correct + wrong);
    const unattempted = Math.max(0, questions - attempted);
    const negativeMarks = sumOf("negative_marks");
    const overTime = sumOf("over_time");

    const bestExam = examRows.reduce((b, e) => (b === null || num(e.marks) > num(b.marks) ? e : b), null);
    const weakestExam = examRows.reduce((b, e) => (b === null || num(e.marks) < num(b.marks) ? e : b), null);
    const firstExam = examRows.length ? examRows[0] : null;
    const latestExam = examRows.length ? examRows[examRows.length - 1] : null;
    const examIndexOf = (e) => (e ? examRows.indexOf(e) + 1 : null);

    const overallPct = maxMarks > 0 ? r1((scored / maxMarks) * 100) : null;
    const avgAccuracy = mean(accList) === null ? null : r1(mean(accList));
    const avgMarks = mean(marksList) === null ? null : r1(mean(marksList));
    const avgTotalMarks = examRows.length
        ? mean(examRows.filter((e) => num(e.total_marks) > 0).map((e) => num(e.total_marks)))
        : null;

    const scoreTrend = halvesTrend(pctList);
    const rankTrend = halvesTrend(rankList, { betterIsLower: true, band: 0.5, round: Math.round });
    const accuracyTrend = halvesTrend(accList);

    // First exam → latest exam, the "Score Improvement" KPI. Percentage points when both
    // papers' totals are known, else relative growth on raw marks.
    let improvementPct = null;
    let improvementMarks = null;
    if (firstExam && latestExam && firstExam !== latestExam) {
        const fp = examPct(firstExam);
        const lp = examPct(latestExam);
        if (isNum(firstExam.marks) && isNum(latestExam.marks)) {
            improvementMarks = r1(num(latestExam.marks) - num(firstExam.marks));
        }
        if (fp !== null && lp !== null) improvementPct = r1(lp - fp);
        else if (improvementMarks !== null && num(firstExam.marks) !== 0) {
            improvementPct = r1((improvementMarks / Math.abs(num(firstExam.marks))) * 100);
        }
    }
    // Growth relative to the first exam's own marks — the "(+42.5%)" style figure.
    const improvementRel =
        improvementMarks !== null && firstExam && num(firstExam.marks) !== 0
            ? r1((improvementMarks / Math.abs(num(firstExam.marks))) * 100)
            : null;

    /* --- time ------------------------------------------------------------- */
    const bd = (metrics && metrics.breakdown) || null;
    const hasTimeData = !!(metrics && metrics.has_time_data !== false && (num(metrics.avg_time_per_question) > 0 || (bd && num(bd.total_time_seconds) > 0)));
    const examsConsidered = bd && num(bd.exams_considered) > 0 ? num(bd.exams_considered) : examRows.length;
    const avgMinutesPerExam =
        bd && num(bd.total_time_seconds) > 0 && examsConsidered
            ? num(bd.total_time_seconds) / 60 / examsConsidered
            : metrics && num(metrics.avg_time_per_question) > 0 && examRows.length
                ? (num(metrics.avg_time_per_question) * attempted) / examRows.length
                : null;
    // Questions answered without running over the expected time, as a share of attempts.
    const timeEfficiency = attempted > 0 && overTime >= 0 ? Math.max(0, r1(100 - (overTime / attempted) * 100)) : null;

    /* --- consistency / spread --------------------------------------------- */
    const stdDev =
        bd && isNum(bd.consistency_std)
            ? r2(num(bd.consistency_std))
            : marksList.length >= 2
                ? (() => {
                    const m = mean(marksList);
                    return r2(Math.sqrt(mean(marksList.map((x) => (x - m) * (x - m)))));
                })()
                : null;
    const consistency =
        metrics && isNum(metrics.consistency)
            ? r1(num(metrics.consistency))
            : pctList.length >= 2
                ? (() => {
                    const m = mean(pctList);
                    const sd = Math.sqrt(mean(pctList.map((p) => (p - m) * (p - m))));
                    return Math.max(0, Math.min(100, r1(100 - (m > 0 ? (sd / m) * 100 : 0))));
                })()
                : null;

    /* --- subject-wise ------------------------------------------------------ */
    const series = subjectSeriesFrom(matrix);
    const speedBy = new Map((speed || []).map((s) => [norm(s.subject), s]));
    // subject_max is the only source of a real per-subject denominator, so the subject
    // "Average %" is computed from it rather than invented.
    const maxBy = new Map();
    (subjectTrend || []).forEach((t) => {
        const maxes = (t.points || []).map((p) => num(p.subject_max)).filter((x) => x > 0);
        if (maxes.length) maxBy.set(norm(t.subject), Math.max(...maxes));
        const marks = (t.points || []).map((p) => (isNum(p.marks) ? num(p.marks) : null)).filter((x) => x !== null);
        if (marks.length && !series.has(norm(t.subject))) {
            series.set(norm(t.subject), { subject: t.subject, points: marks.map((m) => ({ score: m, accuracy: null, attempted: 0 })) });
        }
    });

    const buildSubject = (name, rowFromPerf) => {
        const key = norm(name);
        const points = (series.get(key) || { points: [] }).points;
        const scores = points.map((p) => p.score).filter((v) => v !== null);
        const accs = points.map((p) => p.accuracy).filter((v) => v !== null);
        const sp = speedBy.get(key) || null;
        const accuracy = rowFromPerf && isNum(rowFromPerf.accuracy)
            ? num(rowFromPerf.accuracy)
            : sp && isNum(sp.accuracy)
                ? num(sp.accuracy)
                : mean(accs) === null ? null : r1(mean(accs));
        const avgScore = mean(scores) === null ? null : r1(mean(scores));
        const subjectMax = maxBy.get(key) || null;
        const perExamAttempts = sp && num(sp.attempted) > 0 && examRows.length ? num(sp.attempted) / examRows.length : null;
        return {
            subject: name,
            avgScore,
            subjectMax,
            avgPct: avgScore !== null && subjectMax ? r1((avgScore / subjectMax) * 100) : null,
            bestScore:
                rowFromPerf && isNum(rowFromPerf.best_score) ? num(rowFromPerf.best_score) : scores.length ? Math.max(...scores) : null,
            lowestScore:
                rowFromPerf && isNum(rowFromPerf.lowest_score) ? num(rowFromPerf.lowest_score) : scores.length ? Math.min(...scores) : null,
            sectionAvg: rowFromPerf && isNum(rowFromPerf.section_avg) ? num(rowFromPerf.section_avg) : null,
            accuracy,
            exams: scores.length,
            avgMinutes:
                sp && sp.has_time_data !== false && num(sp.avg_time_per_question) > 0 && perExamAttempts
                    ? num(sp.avg_time_per_question) * perExamAttempts
                    : null,
            trend: halvesTrend(accs.length >= 2 ? accs : scores),
            strength:
                accuracy === null ? null : accuracy >= 75 ? "Strong" : accuracy >= 60 ? "Good" : accuracy >= 45 ? "Average" : "Needs Work",
        };
    };

    const subjectStats = subjectRows.map((s) => buildSubject(s.subject, s));
    // Subjects the matrix / trend know about but subjectPerformance didn't return — a
    // missing subject would understate the analysis.
    series.forEach((v, key) => {
        if (subjectStats.some((s) => norm(s.subject) === key)) return;
        subjectStats.push(buildSubject(v.subject, null));
    });

    const rankedSubjects = subjectStats.filter((s) => s.accuracy !== null).sort((a, b) => b.accuracy - a.accuracy);
    const bestSubject = (snapshot && snapshot.best_subject) || (rankedSubjects.length ? rankedSubjects[0].subject : null);
    const weakSubject =
        (snapshot && snapshot.weak_subject) || (rankedSubjects.length ? rankedSubjects[rankedSubjects.length - 1].subject : null);
    const byScore = subjectStats.filter((s) => s.avgScore !== null).sort((a, b) => b.avgScore - a.avgScore);
    const topScoringSubject = byScore.length ? byScore[0] : null;

    /* --- topics ------------------------------------------------------------ */
    // Aggregate duplicates (the same topic can appear per chapter/exam) weighted by
    // attempts, then keep the strongest and weakest so the bar panel shows both ends.
    const topicMap = new Map();
    (topics || []).forEach((t) => {
        if (!isNum(t.accuracy_percentage)) return;
        const key = norm(t.topic) || norm(t.chapter);
        if (!key) return;
        const w = Math.max(1, num(t.attempted));
        const prev = topicMap.get(key) || { topic: t.topic || t.chapter, subject: t.subject, acc: 0, w: 0, attempted: 0 };
        topicMap.set(key, {
            topic: prev.topic,
            subject: prev.subject || t.subject,
            acc: prev.acc + num(t.accuracy_percentage) * w,
            w: prev.w + w,
            attempted: prev.attempted + num(t.attempted),
        });
    });
    const topicStats = [...topicMap.values()]
        .map((t) => ({ topic: t.topic, subject: t.subject, accuracy: r1(t.acc / t.w), attempted: t.attempted }))
        .sort((a, b) => b.accuracy - a.accuracy);
    const topicsShown = topicStats.length > 8 ? [...topicStats.slice(0, 5), ...topicStats.slice(-3)] : topicStats;

    /* --- attendance -------------------------------------------------------- */
    const attendancePct =
        attendance && isNum(attendance.percentage)
            ? num(attendance.percentage)
            : snapshot && isNum(snapshot.attendance)
                ? num(snapshot.attendance)
                : null;

    /* --- exam availability ------------------------------------------------- */
    const streamTotals = (streams || []).reduce(
        (acc, s) => ({ total: acc.total + num(s.total_exams), completed: acc.completed + num(s.completed) }),
        { total: 0, completed: 0 }
    );
    const examsAvailable = streamTotals.total > 0 ? streamTotals.total : null;

    /* --- academic areas ---------------------------------------------------- */
    // "Area" means a dimension of exam behaviour, distinct from a subject. Each is scored
    // 0-100 from figures printed elsewhere in the report, so the verdict is reproducible.
    const coverage = questions > 0 ? r1((attempted / questions) * 100) : null;
    const rankStanding = (() => {
        const standings = examRows
            .filter((e) => num(e.section_rank) > 0 && num(e.section_total) > 0)
            .map((e) => (1 - num(e.section_rank) / num(e.section_total)) * 100);
        return standings.length ? r1(mean(standings)) : null;
    })();
    const areas = [
        { area: "Answer accuracy", score: avgAccuracy },
        { area: "Consistency across exams", score: consistency },
        { area: "Paper coverage", score: coverage },
        { area: "Standing within the section", score: rankStanding },
        { area: "Attendance", score: attendancePct === null ? null : r1(attendancePct) },
        { area: "Time management", score: timeEfficiency },
    ].filter((a) => a.score !== null);
    const areasRanked = [...areas].sort((a, b) => b.score - a.score);

    const period = (() => {
        const from = firstExam ? parseExamMoment(firstExam.date) : null;
        const to = latestExam ? parseExamMoment(latestExam.date) : null;
        if (!from && !to) return null;
        return `${from ? from.format("DD MMM YYYY") : "—"} – ${to ? to.format("DD MMM YYYY") : "—"}`;
    })();

    return {
        examRows,
        examCount: examRows.length,
        examsAvailable,
        period,
        avgMarks,
        avgTotalMarks: avgTotalMarks === null ? null : Math.round(avgTotalMarks),
        bestMarks: marksList.length ? Math.max(...marksList) : null,
        lowestMarks: marksList.length ? Math.min(...marksList) : null,
        bestExam,
        weakestExam,
        bestExamIndex: examIndexOf(bestExam),
        weakestExamIndex: examIndexOf(weakestExam),
        firstExam,
        latestExam,
        totalScored: marksList.length ? r1(scored) : null,
        totalMaxMarks: maxMarks || null,
        overallPct,
        grade: gradeFor(overallPct === null ? avgAccuracy : overallPct),
        level: levelFor(overallPct === null ? avgAccuracy : overallPct),
        avgAccuracy,
        attemptAccuracy: attempted > 0 ? r1((correct / attempted) * 100) : null,
        avgRank: mean(rankList) === null ? null : Math.round(mean(rankList)),
        bestRank: rankList.length ? Math.min(...rankList) : null,
        questions,
        correct,
        wrong,
        attempted,
        unattempted,
        unattemptedShare: questions > 0 ? r1((unattempted / questions) * 100) : null,
        negativeMarks,
        overTime,
        hasTimeData,
        avgMinutesPerExam,
        avgMinutesPerQuestion: metrics && num(metrics.avg_time_per_question) > 0 ? r2(num(metrics.avg_time_per_question)) : null,
        timedQuestions: bd && isNum(bd.total_questions_timed) ? num(bd.total_questions_timed) : null,
        timeEfficiency,
        attemptRate: metrics && isNum(metrics.attempt_rate) ? r1(num(metrics.attempt_rate)) : coverage,
        stdDev,
        consistency,
        scoreTrend,
        rankTrend,
        accuracyTrend,
        improvementPct,
        improvementMarks,
        improvementRel,
        rankBaseline: bd && num(bd.rank_baseline) > 0 ? num(bd.rank_baseline) : rankList.length ? rankList[0] : null,
        rankLatest: bd && num(bd.rank_latest) > 0 ? num(bd.rank_latest) : rankList.length ? rankList[rankList.length - 1] : null,
        subjectStats,
        bestSubject,
        weakSubject,
        topScoringSubject,
        topicStats,
        topicsShown,
        attendance: attendance || null,
        attendancePct,
        areas,
        strongestArea: areasRanked.length ? areasRanked[0] : null,
        weakestArea: areasRanked.length > 1 ? areasRanked[areasRanked.length - 1] : null,
        coverage,
        snapshot: snapshot || null,
    };
};

/* ------------------------------------------------------------- svg building */

const polar = (cx, cy, r, deg) => [cx + r * Math.cos((Math.PI * deg) / 180), cy - r * Math.sin((Math.PI * deg) / 180)];

// Semicircular gauge with coloured bands and a needle — the "Overall Performance" dial.
const gauge = (pct) => {
    const W = 150;
    const H = 92;
    const cx = W / 2;
    const cy = 78;
    const r = 58;
    const t = 13;
    const bands = [
        [0, 40, "#ef4444"],
        [40, 60, "#f97316"],
        [60, 75, "#f59e0b"],
        [75, 90, "#84cc16"],
        [90, 100, "#16a34a"],
    ];
    const ang = (v) => 180 - Math.max(0, Math.min(100, v)) * 1.8;
    const arcs = bands
        .map(([a, b, color]) => {
            const [x0, y0] = polar(cx, cy, r, ang(a));
            const [x1, y1] = polar(cx, cy, r, ang(b));
            return `<path d="M ${r2(x0)} ${r2(y0)} A ${r} ${r} 0 0 1 ${r2(x1)} ${r2(y1)}" stroke="${color}" stroke-width="${t}" fill="none" stroke-linecap="butt"/>`;
        })
        .join("");
    const needle =
        pct === null
            ? ""
            : (() => {
                const [nx, ny] = polar(cx, cy, r - t - 3, ang(pct));
                return `<line x1="${cx}" y1="${cy}" x2="${r2(nx)}" y2="${r2(ny)}" stroke="#0f172a" stroke-width="2.4" stroke-linecap="round"/>
                        <circle cx="${cx}" cy="${cy}" r="3.6" fill="#0f172a"/>`;
            })();
    return `<svg viewBox="0 0 ${W} ${H}" class="srd-gauge-svg" role="img">
      ${arcs}${needle}
      <text x="${cx}" y="${cy - 12}" text-anchor="middle" font-size="20" font-weight="800" fill="#16a34a">${pct === null ? "—" : `${r1(pct)}%`}</text>
    </svg>`;
};

// Donut for the question split.
const donut = (segments) => {
    const size = 108;
    const r = 38;
    const t = 20;
    const c = 2 * Math.PI * r;
    const totalVal = total(segments.map((s) => num(s.value))) || 1;
    let offset = 0;
    const rings = segments
        .map((s) => {
            const frac = num(s.value) / totalVal;
            const len = frac * c;
            const seg = `<circle cx="${size / 2}" cy="${size / 2}" r="${r}" fill="none" stroke="${s.color}" stroke-width="${t}"
              stroke-dasharray="${r2(len)} ${r2(c - len)}" stroke-dashoffset="${r2(-offset)}" transform="rotate(-90 ${size / 2} ${size / 2})"/>`;
            offset += len;
            return seg;
        })
        .join("");
    return `<svg viewBox="0 0 ${size} ${size}" class="srd-donut-svg" role="img">
      <circle cx="${size / 2}" cy="${size / 2}" r="${r}" fill="none" stroke="#f1f5f9" stroke-width="${t}"/>
      ${rings}
    </svg>`;
};

// Line chart with a value label above every point — used for the score trend and the rank
// progression (rank is plotted on a normal axis, so a falling line = improvement).
const lineChart = (points, { color = "#2563eb", yMax, yTicks = 5, yLabel, fmt = (v) => String(v), height = 132 } = {}) => {
    const usable = points.filter((p) => p.value !== null && p.value !== undefined);
    if (usable.length === 0) return `<div class="srd-nodata">No data to plot.</div>`;
    const W = 520;
    const H = height;
    const padL = 34;
    const padR = 10;
    const padT = 16;
    const padB = 20;
    const plotW = W - padL - padR;
    const plotH = H - padT - padB;
    const maxVal = yMax || Math.max(...usable.map((p) => num(p.value))) * 1.15 || 1;
    const n = points.length;
    const x = (i) => padL + (n === 1 ? plotW / 2 : (plotW * i) / (n - 1));
    const y = (v) => padT + plotH - (Math.max(0, Math.min(maxVal, num(v))) / maxVal) * plotH;

    const ticks = Array.from({ length: yTicks + 1 }, (_, i) => (maxVal / yTicks) * i);
    const grid = ticks
        .map(
            (t) =>
                `<line x1="${padL}" y1="${r2(y(t))}" x2="${W - padR}" y2="${r2(y(t))}" stroke="#eef2f7" stroke-width="0.7"/>
                 <text x="${padL - 4}" y="${r2(y(t)) + 2.5}" text-anchor="end" font-size="6" fill="#94a3b8">${Math.round(t)}</text>`
        )
        .join("");
    const path = usable.map((p) => `${r2(x(points.indexOf(p)))},${r2(y(p.value))}`).join(" ");
    const dots = usable
        .map((p) => {
            const i = points.indexOf(p);
            return `<circle cx="${r2(x(i))}" cy="${r2(y(p.value))}" r="2.4" fill="#fff" stroke="${color}" stroke-width="1.6"/>
                    <text x="${r2(x(i))}" y="${r2(y(p.value)) - 5}" text-anchor="middle" font-size="6.4" font-weight="700" fill="#334155">${esc(fmt(p.value))}</text>`;
        })
        .join("");
    const labels =
        n <= 12
            ? points
                .map((p, i) => `<text x="${r2(x(i))}" y="${H - 5}" text-anchor="middle" font-size="6" fill="#64748b">${esc(p.label)}</text>`)
                .join("")
            : `<text x="${W / 2}" y="${H - 5}" text-anchor="middle" font-size="6" fill="#94a3b8">Exam 1 → ${n} (oldest to latest)</text>`;
    return `<svg viewBox="0 0 ${W} ${H}" class="srd-line-svg" role="img">
      ${grid}
      ${yLabel ? `<text x="2" y="10" font-size="6" fill="#94a3b8">${esc(yLabel)}</text>` : ""}
      <polyline points="${path}" fill="none" stroke="${color}" stroke-width="1.7" stroke-linejoin="round"/>
      ${dots}${labels}
    </svg>`;
};

/* ------------------------------------------------------------------- markup */

const panel = (title, sub, body, cls) =>
    `<div class="srd-panel${cls ? ` ${cls}` : ""}">
       <div class="srd-panel-hd"><span class="srd-panel-t">${esc(title)}</span>${sub ? `<span class="srd-panel-s">${esc(sub)}</span>` : ""}</div>
       <div class="srd-panel-bd">${body}</div>
     </div>`;

const kpi = (glyph, tone, label, value, unit, note) =>
    `<div class="srd-kpi">
       <div class="srd-kpi-ic srd-ic-${tone}">${glyph}</div>
       <div class="srd-kpi-l">${esc(label)}</div>
       <div class="srd-kpi-v srd-fg-${tone}">${esc(value)}${unit ? `<span class="srd-kpi-u"> ${esc(unit)}</span>` : ""}</div>
       ${note ? `<div class="srd-kpi-n">${esc(note)}</div>` : ""}
     </div>`;

const idLine = (glyph, label, value) =>
    `<div class="srd-id-line"><span class="srd-id-g">${glyph}</span><span class="srd-id-k">${esc(label)}</span><span class="srd-id-c">:</span><span class="srd-id-v">${esc(value)}</span></div>`;

const statLine = (glyph, tone, label, value, strong) =>
    `<div class="srd-stat"><span class="srd-stat-g srd-fg-${tone}">${glyph}</span><span class="srd-stat-l">${esc(label)}</span><span class="srd-stat-v${strong ? ` srd-fg-${tone}` : ""}">${value}</span></div>`;

const chip = (text, tone) => `<div class="srd-chip srd-chip-${tone || "blue"}">${esc(text)}</div>`;

/* Header band + student card ------------------------------------------------ */
const headerBand = (school, logo, examStreamLabel, sum) => `
  <div class="srd-band">
    <div class="srd-band-l">
      <div class="srd-band-t">Student Overall Performance Report</div>
      <div class="srd-band-s">${esc(sum.examCount ? `Analysis of ${sum.examCount === 1 ? "1 exam" : `all ${sum.examCount} exams`}` : "No exams on record")}</div>
    </div>
    <div class="srd-band-r">
      ${logo ? `<img class="srd-band-logo" src="${esc(logo)}" alt="" />` : `<span class="srd-band-cap">◈</span>`}
      <div>
        <div class="srd-band-org">${esc(school)}</div>
        ${examStreamLabel ? `<div class="srd-band-prog">${esc(examStreamLabel)}</div>` : ""}
      </div>
    </div>
  </div>`;

const studentCard = (student, school, sum) => {
    const s = student || {};
    const photo = s.profile_image_base || s.profile_image || "";
    const initial = studentName(s).charAt(0).toUpperCase();
    const classText = [s.class_name, s.section_name ? `(${s.section_name})` : null].filter(Boolean).join(" ") || "—";
    const batch = val(s.batch_name || s.group_name || s.section_name);
    const examsText = sum.examsAvailable ? `${sum.examCount} / ${sum.examsAvailable}` : String(sum.examCount);
    return `
    <div class="srd-card">
      <div class="srd-card-id">
        ${photo
            ? `<img class="srd-photo" src="${esc(photo)}" alt="" />`
            : `<div class="srd-photo srd-photo-ph">${esc(initial)}</div>`}
        <div class="srd-id-body">
          <div class="srd-name">${esc(studentName(s))}</div>
          ${idLine("▤", "Roll No.", val(s.roll_no))}
          ${idLine("◈", "Admission No.", val(s.admission_no || s.admissionNo))}
          ${idLine("▦", "Class", classText)}
          ${idLine("◉", "Batch", batch)}
          ${idLine("⌂", "Institute", school)}
        </div>
      </div>
      <div class="srd-card-cell">
        <div class="srd-cell-g">▤</div>
        <div class="srd-cell-l">Exams Attempted</div>
        <div class="srd-cell-v">${esc(examsText)}</div>
        <div class="srd-cell-sub">Period</div>
        <div class="srd-cell-sub2">${esc(sum.period || "—")}</div>
      </div>
      <div class="srd-card-cell srd-cell-grade">
        <div class="srd-cell-l">Overall Grade</div>
        <div class="srd-grade">
          <span class="srd-laurel">❰</span>
          <span class="srd-grade-v">${esc(sum.grade || "—")}</span>
          <span class="srd-laurel">❱</span>
        </div>
        <div class="srd-cell-sub2">${esc(sum.level || "awaiting exams")}</div>
      </div>
      <div class="srd-card-cell srd-cell-gauge">
        ${gauge(sum.overallPct === null ? (sum.avgAccuracy === null ? null : sum.avgAccuracy) : sum.overallPct)}
        <div class="srd-cell-sub2">Overall Performance</div>
      </div>
    </div>`;
};

/* KPI strip ---------------------------------------------------------------- */
const kpiStrip = (sum) => {
    const improvement =
        sum.improvementPct === null
            ? "—"
            : `${sum.improvementPct > 0 ? "+" : ""}${sum.improvementPct}%`;
    return `<div class="srd-kpis">
      ${kpi("◎", "blue", "Average Score", sum.avgMarks === null ? "—" : fmtMarks(sum.avgMarks), sum.avgTotalMarks ? `/ ${sum.avgTotalMarks}` : "", "per exam")}
      ${kpi("▲", "green", "Overall Percentage", sum.overallPct === null ? "—" : `${sum.overallPct}%`, "", "marks obtained / total")}
      ${kpi("✪", "violet", "Average Rank", sum.avgRank === null ? "—" : grouped(sum.avgRank), "", sum.bestRank === null ? "within exam cohort" : `best ${sum.bestRank}`)}
      ${kpi("✓", "sky", "Average Accuracy", sum.avgAccuracy === null ? "—" : `${sum.avgAccuracy}%`, "", sum.attemptAccuracy === null ? "" : `${sum.attemptAccuracy}% of attempted`)}
      ${kpi("◷", "orange", "Average Time / Exam", sum.hasTimeData ? fmtDuration(sum.avgMinutesPerExam) : "—", "", sum.hasTimeData ? "recorded attempts" : "not recorded offline")}
      ${kpi("↗", "green", "Score Improvement", improvement, "", sum.examCount > 1 ? `exam 1 to exam ${sum.examCount}` : "needs 2+ exams")}
      ${kpi("★", "violet", "Highest Score", sum.bestMarks === null ? "—" : fmtMarks(sum.bestMarks), sum.avgTotalMarks ? `/ ${sum.avgTotalMarks}` : "", sum.bestExamIndex ? `in exam ${sum.bestExamIndex}` : "")}
      ${kpi("↘", "red", "Lowest Score", sum.lowestMarks === null ? "—" : fmtMarks(sum.lowestMarks), sum.avgTotalMarks ? `/ ${sum.avgTotalMarks}` : "", sum.weakestExamIndex ? `in exam ${sum.weakestExamIndex}` : "")}
    </div>`;
};

/* Score trend -------------------------------------------------------------- */
const scoreTrendPanel = (sum) => {
    const points = sum.examRows.map((e, i) => ({ label: `Exam ${i + 1}`, value: isNum(e.marks) ? num(e.marks) : null }));
    const maxTotal = Math.max(...sum.examRows.map((e) => num(e.total_marks)), 0);
    const chart = lineChart(points, {
        color: "#2563eb",
        yMax: maxTotal > 0 ? maxTotal : undefined,
        yLabel: maxTotal > 0 ? `Score (out of ${Math.round(maxTotal)})` : "Score",
        fmt: (v) => fmtMarks(v),
    });
    const footer =
        sum.improvementMarks === null
            ? sum.examCount === 1
                ? chip("Single exam on record — a trend needs at least two.", "grey")
                : ""
            : chip(
                `Overall Improvement : ${sum.improvementMarks > 0 ? "+" : ""}${fmtMarks(sum.improvementMarks)} Marks${sum.improvementRel === null ? "" : ` (${sum.improvementRel > 0 ? "+" : ""}${sum.improvementRel}%)`}`,
                sum.improvementMarks > 0 ? "green" : sum.improvementMarks < 0 ? "red" : "grey"
            );
    return panel("Score Trend", sum.examCount ? `All ${sum.examCount} exams` : null, `${chart}${footer}`);
};

/* Subject-wise table ------------------------------------------------------- */
const subjectPanel = (sum) => {
    const anyTime = sum.subjectStats.some((s) => s.avgMinutes !== null);
    const anyMax = sum.subjectStats.some((s) => s.subjectMax);
    const rows = sum.subjectStats
        .map(
            (s) => `<tr>
        <td class="srd-l srd-subj">${esc(val(s.subject))}</td>
        <td class="srd-b">${s.avgScore === null ? "—" : fmtMarks(s.avgScore)}${s.subjectMax ? `<span class="srd-mut"> / ${Math.round(s.subjectMax)}</span>` : ""}</td>
        <td>${s.avgPct === null ? "—" : `${s.avgPct}%`}</td>
        <td>${s.accuracy === null ? "—" : `${r1(s.accuracy)}%`}</td>
        ${anyTime ? `<td>${s.avgMinutes === null ? "—" : fmtDuration(s.avgMinutes)}</td>` : ""}
        <td>${s.strength ? `<span class="srd-pill srd-pill-${norm(s.strength).replace(/\s+/g, "-")}">${esc(s.strength)}</span>` : "—"}</td>
      </tr>`
        )
        .join("");
    const totalsRow = `<tr class="srd-tot">
        <td class="srd-l">Overall</td>
        <td class="srd-b">${sum.avgMarks === null ? "—" : fmtMarks(sum.avgMarks)}${sum.avgTotalMarks ? `<span class="srd-mut"> / ${sum.avgTotalMarks}</span>` : ""}</td>
        <td>${sum.overallPct === null ? "—" : `${sum.overallPct}%`}</td>
        <td>${sum.avgAccuracy === null ? "—" : `${sum.avgAccuracy}%`}</td>
        ${anyTime ? `<td>${sum.hasTimeData ? fmtDuration(sum.avgMinutesPerExam) : "—"}</td>` : ""}
        <td>${sum.level ? `<span class="srd-pill srd-pill-${norm(sum.level).replace(/\s+/g, "-")}">${esc(sum.level)}</span>` : "—"}</td>
      </tr>`;
    const body = `<table class="srd-tbl">
        <thead><tr>
          <th class="srd-l">Subject</th><th>Average Score</th><th>Average %</th><th>Accuracy %</th>
          ${anyTime ? "<th>Avg Time / Exam</th>" : ""}<th>Strength</th>
        </tr></thead>
        <tbody>${rows || `<tr><td colspan="${anyTime ? 6 : 5}" class="srd-empty">No subject-wise data available.</td></tr>`}${rows ? totalsRow : ""}</tbody>
      </table>
      ${anyMax ? "" : `<div class="srd-note">Average % is shown only where the paper records a per-subject maximum; elsewhere accuracy is the reliable percentage.</div>`}`;
    return panel("Subject-wise Performance", "Averaged over every exam", body);
};

/* Question analysis -------------------------------------------------------- */
const questionPanel = (sum) => {
    const share = (v) => (sum.questions > 0 ? `${r1((v / sum.questions) * 100)}%` : "—");
    const body = `<div class="srd-donut-wrap">
        ${donut([
        { value: sum.correct, color: "#22c55e" },
        { value: sum.wrong, color: "#ef4444" },
        { value: sum.unattempted, color: "#f59e0b" },
    ])}
        <div class="srd-donut-legend">
          <div class="srd-lgi"><span class="srd-dot srd-dot-green">✓</span>Correct<b>${grouped(sum.correct)}</b><span class="srd-mut">(${share(sum.correct)})</span></div>
          <div class="srd-lgi"><span class="srd-dot srd-dot-red">✕</span>Incorrect<b>${grouped(sum.wrong)}</b><span class="srd-mut">(${share(sum.wrong)})</span></div>
          <div class="srd-lgi"><span class="srd-dot srd-dot-amber">○</span>Unattempted<b>${grouped(sum.unattempted)}</b><span class="srd-mut">(${share(sum.unattempted)})</span></div>
          <div class="srd-lgi srd-lgi-tot">Total Questions<b>${grouped(sum.questions)}</b></div>
        </div>
      </div>
      ${chip(`Overall Accuracy : ${sum.avgAccuracy === null ? "—" : `${sum.avgAccuracy}%`}`, "green")}`;
    return panel("Question Analysis", "Overall", body);
};

/* Rank progression --------------------------------------------------------- */
const rankPanel = (sum) => {
    const points = sum.examRows.map((e, i) => ({ label: `Exam ${i + 1}`, value: num(e.exam_rank) > 0 ? num(e.exam_rank) : null }));
    const has = points.some((p) => p.value !== null);
    const moved =
        sum.rankBaseline !== null && sum.rankLatest !== null ? Math.round(sum.rankBaseline - sum.rankLatest) : null;
    const badge =
        moved === null
            ? ""
            : `<div class="srd-badge srd-badge-${moved > 0 ? "green" : moved < 0 ? "red" : "grey"}">
                 <span class="srd-badge-t">${moved > 0 ? "Improvement in rank" : moved < 0 ? "Drop in rank" : "Rank unchanged"}</span>
                 <span class="srd-badge-v">${Math.abs(moved)} place${Math.abs(moved) === 1 ? "" : "s"}</span>
               </div>`;
    const body = has
        ? `<div class="srd-rank-top">${badge}</div>
           ${lineChart(points, { color: "#2563eb", yLabel: "Rank", fmt: (v) => grouped(v) })}
           ${chip(
            sum.rankTrend
                ? sum.rankTrend.label === "Improving"
                    ? `Rank improving across the series — average ${grouped(sum.rankTrend.from)} → ${grouped(sum.rankTrend.to)}`
                    : sum.rankTrend.label === "Declining"
                        ? `Rank slipping — average ${grouped(sum.rankTrend.from)} → ${grouped(sum.rankTrend.to)}`
                        : `Rank holding steady around ${grouped(sum.rankTrend.to)}`
                : "Rank recorded for a single exam only.",
            sum.rankTrend && sum.rankTrend.label === "Improving" ? "green" : sum.rankTrend && sum.rankTrend.label === "Declining" ? "red" : "grey"
        )}`
        : `<div class="srd-nodata">No rank recorded for these exams.</div>`;
    return panel("Rank Progression", "Lower is better", body);
};

/* Attendance overview ------------------------------------------------------ */
const attendancePanel = (sum) => {
    const a = sum.attendance || {};
    if (!sum.attendance && sum.attendancePct === null) {
        return panel("Attendance Overview", "All-time", `<div class="srd-nodata">No attendance record available for this student.</div>`);
    }
    const pct = sum.attendancePct;
    const tone = pct === null ? "blue" : pct >= 85 ? "green" : pct >= 75 ? "amber" : "red";
    const verdict = pct === null ? "" : pct >= 85 ? "Regular" : pct >= 75 ? "Watch — near the limit" : "Below the 75% mark";
    const present = isNum(a.total_present) ? num(a.total_present) : null;
    const absent = isNum(a.total_absent) ? num(a.total_absent) : null;
    const marked = isNum(a.total_marked_days) ? num(a.total_marked_days) : present !== null && absent !== null ? present + absent : null;
    // Present / absent split of the days actually marked — the only honest denominator for
    // this bar (working days may include days nobody has marked yet).
    const bar =
        marked && present !== null
            ? `<div class="srd-split">
                 <span class="srd-split-p" style="width:${Math.max(1, Math.min(100, (present / marked) * 100))}%"></span>
                 <span class="srd-split-a"></span>
               </div>
               <div class="srd-split-lg"><span class="srd-fg-green">■ Present ${intText(present)}</span><span class="srd-fg-red">■ Absent ${absent === null ? "—" : intText(absent)}</span></div>`
            : "";
    const body = `
      <div class="srd-att-hero">
        <div class="srd-att-v srd-fg-${tone}">${pct === null ? "—" : `${r1(pct)}%`}</div>
        <div class="srd-att-n">${esc(verdict)}</div>
      </div>
      ${bar}
      ${statLine("▤", "blue", "Total working days", `<b>${intText(a.total_working_days)}</b>`)}
      ${statLine("✓", "green", "Present days", `<b>${intText(a.total_present)}</b>`)}
      ${statLine("✕", "red", "Absent days", `<b>${intText(a.total_absent)}</b>`)}
      ${statLine("◈", "violet", "Days marked so far", `<b>${marked === null ? "—" : intText(marked)}</b>${isNum(a.total_working_days) && marked !== null && marked < num(a.total_working_days) ? ` <span class="srd-mut">of ${intText(a.total_working_days)}</span>` : ""}`)}`;
    return panel("Attendance Overview", "All-time", body);
};

/* Performance summary ------------------------------------------------------ */
const summaryPanel = (sum) => {
    const body = `
      ${statLine("▤", "blue", "Total marks obtained", `<b>${sum.totalScored === null ? "—" : grouped(sum.totalScored)}</b>${sum.totalMaxMarks ? ` <span class="srd-mut">/ ${grouped(sum.totalMaxMarks)}</span>` : ""}`)}
      ${statLine("◎", "blue", "Average marks / exam", `<b>${sum.avgMarks === null ? "—" : fmtMarks(sum.avgMarks)}</b>${sum.avgTotalMarks ? ` <span class="srd-mut">/ ${sum.avgTotalMarks}</span>` : ""}`)}
      ${statLine("★", "green", "Highest marks", `<b>${sum.bestMarks === null ? "—" : fmtMarks(sum.bestMarks)}</b>${sum.bestExamIndex ? ` <span class="srd-mut">(exam ${sum.bestExamIndex})</span>` : ""}`)}
      ${statLine("↘", "red", "Lowest marks", `<b>${sum.lowestMarks === null ? "—" : fmtMarks(sum.lowestMarks)}</b>${sum.weakestExamIndex ? ` <span class="srd-mut">(exam ${sum.weakestExamIndex})</span>` : ""}`)}
      ${statLine("◇", "violet", "Standard deviation", `<b>${sum.stdDev === null ? "—" : sum.stdDev}</b>`)}
      ${statLine("◈", "violet", "Consistency score", `<b>${sum.consistency === null ? "—" : `${sum.consistency}%`}</b>`)}
      ${statLine("◉", "sky", "Attempt rate", `<b>${sum.attemptRate === null ? "—" : `${sum.attemptRate}%`}</b>${sum.unattemptedShare === null ? "" : ` <span class="srd-mut">${sum.unattemptedShare}% untouched</span>`}`)}
      ${statLine("▼", "red", "Lost to negative marking", `<b>${sum.negativeMarks > 0 ? fmtMarks(sum.negativeMarks) : "—"}</b>`)}
    `;
    return panel("Performance Summary", "Cumulative", body);
};

/* Insights ----------------------------------------------------------------- */
const insightsPanel = (sum) => {
    const strengths = [];
    const improve = [];
    const recommend = [];

    sum.subjectStats
        .filter((s) => s.accuracy !== null && s.accuracy >= 65)
        .sort((a, b) => b.accuracy - a.accuracy)
        .slice(0, 2)
        .forEach((s) => strengths.push(`Strong grasp of ${s.subject} — ${r1(s.accuracy)}% accuracy.`));
    sum.topicStats.slice(0, 2).forEach((t) => {
        if (t.accuracy >= 80) strengths.push(`${t.topic} is well mastered (${r1(t.accuracy)}%).`);
    });
    if (sum.scoreTrend && sum.scoreTrend.label === "Improving") strengths.push(`Consistent improvement in performance — later exams average ${sum.scoreTrend.delta}% points higher.`);
    if (sum.rankTrend && sum.rankTrend.label === "Improving") strengths.push(`Rank has improved from ${grouped(sum.rankTrend.from)} to ${grouped(sum.rankTrend.to)} on average.`);
    if (sum.attemptAccuracy !== null && sum.attemptAccuracy >= 75) strengths.push(`Converts attempts reliably — ${sum.attemptAccuracy}% of attempted questions correct.`);
    if (sum.consistency !== null && sum.consistency >= 80) strengths.push(`Steady output across exams (consistency ${sum.consistency}%).`);
    if (sum.attendancePct !== null && sum.attendancePct >= 85) strengths.push(`Regular attendance at ${r1(sum.attendancePct)}%.`);

    sum.subjectStats
        .filter((s) => s.accuracy !== null && s.accuracy < 55)
        .sort((a, b) => a.accuracy - b.accuracy)
        .slice(0, 2)
        .forEach((s) => improve.push(`Focus more on ${s.subject} — ${r1(s.accuracy)}% accuracy.`));
    sum.topicStats
        .slice(-2)
        .filter((t) => t.accuracy < 70)
        .forEach((t) => improve.push(`Practise more questions on ${t.topic} (${r1(t.accuracy)}%).`));
    if (sum.unattemptedShare !== null && sum.unattemptedShare > 10) improve.push(`${sum.unattemptedShare}% of questions are left unattempted — pace and coverage.`);
    if (sum.negativeMarks > 0 && sum.attemptAccuracy !== null && sum.attemptAccuracy < 70) improve.push(`${fmtMarks(sum.negativeMarks)} marks lost to negative marking.`);
    if (sum.attendancePct !== null && sum.attendancePct < 75) improve.push(`Attendance at ${r1(sum.attendancePct)}% is below the 75% mark.`);
    if (sum.scoreTrend && sum.scoreTrend.label === "Declining") improve.push(`Scores have slipped ${Math.abs(sum.scoreTrend.delta)}% points against the earlier exams.`);
    if (sum.consistency !== null && sum.consistency < 70) improve.push(`Performance swings between exams (consistency ${sum.consistency}%).`);

    if (sum.weakSubject) recommend.push(`Give ${sum.weakSubject} the first revision slot each day and re-attempt its past exam questions.`);
    if (sum.unattemptedShare !== null && sum.unattemptedShare > 10) recommend.push(`Practise full papers against the clock — untouched questions cap the score before accuracy matters.`);
    if (sum.scoreTrend && sum.scoreTrend.label === "Improving") recommend.push(`Maintain the current routine; the trajectory is already working.`);
    if (sum.topScoringSubject) recommend.push(`Keep ${sum.topScoringSubject.subject} sharp with light weekly revision so it keeps carrying the total.`);
    if (!recommend.length) recommend.push(`Maintain consistent practice and focus on the weaker topics listed above.`);

    const list = (items, fallback) =>
        items.length
            ? `<ul class="srd-ins-list">${items.slice(0, 4).map((t) => `<li>${esc(t)}</li>`).join("")}</ul>`
            : `<div class="srd-nodata">${esc(fallback)}</div>`;

    const body = `
      <div class="srd-tag srd-tag-green">Strengths</div>
      ${list(strengths, "No clear strengths stand out from the data yet.")}
      <div class="srd-tag srd-tag-amber">Areas to Improve</div>
      ${list(improve, "Nothing flagged from the current data.")}
      <div class="srd-tag srd-tag-blue">Recommendation</div>
      <div class="srd-rec">${esc(recommend.slice(0, 2).join(" "))}</div>`;
    return panel("Performance Insights & Recommendations", "Auto-generated from this report", body);
};

/* Examination table -------------------------------------------------------- */
const examTablePanel = (sum) => {
    const rows = sum.examRows
        .map((e, i) => {
            const p = examPct(e);
            const appeared = num(e.total_questions) > 0 || isNum(e.marks);
            return `<tr>
        <td class="srd-c">${i + 1}</td>
        <td class="srd-l srd-subj">${esc(val(e.exam_name))}</td>
        <td>${esc(fmtExamDate(e.date))}</td>
        <td>${esc(titleCase(e.mode) || "—")}</td>
        <td class="srd-b">${fmtMarks(e.marks)}${num(e.total_marks) > 0 ? `<span class="srd-mut"> / ${fmtMarks(e.total_marks)}</span>` : ""}</td>
        <td>${p === null ? "—" : `${p}%`}</td>
        <td>${pctText(e.accuracy)}</td>
        <td>${num(e.exam_rank) > 0 ? grouped(e.exam_rank) : "—"}</td>
        <td>${num(e.section_rank) > 0 ? `${intText(e.section_rank)}${num(e.section_total) > 0 ? ` / ${intText(e.section_total)}` : ""}` : "—"}</td>
        <td>${appeared ? `<span class="srd-fg-green">Appeared</span>` : `<span class="srd-fg-red">Not appeared</span>`}</td>
      </tr>`;
        })
        .join("");
    const body = `<table class="srd-tbl">
      <thead><tr>
        <th class="srd-c">Exam</th><th class="srd-l">Name</th><th>Date</th><th>Mode</th><th>Score</th>
        <th>%</th><th>Accuracy</th><th>Rank</th><th>Section Rank</th><th>Status</th>
      </tr></thead>
      <tbody>${rows || `<tr><td colspan="10" class="srd-empty">No exam attempts on record.</td></tr>`}</tbody>
    </table>`;
    return panel("Examination History", "Chronological — identifies each point on the charts above", body);
};

/* Prediction footer -------------------------------------------------------- */
const footerStrip = (sum, school, student) => {
    const snap = sum.snapshot || {};
    const air = snap.simulated_air != null && `${snap.simulated_air}` !== "" ? grouped(snap.simulated_air) : null;
    const predicted = isNum(snap.predicted_score)
        ? `${fmtMarks(snap.predicted_score)}${isNum(snap.max_score) ? ` / ${fmtMarks(snap.max_score)}` : ""}`
        : null;
    const targetName = snap.target ? String(snap.target).toUpperCase() : "";
    const risk = snap.risk_level ? titleCase(snap.risk_level) : null;
    const cell = (glyph, tone, label, value, note) =>
        `<div class="srd-foot-cell"><span class="srd-foot-g srd-fg-${tone}">${glyph}</span>
           <div><div class="srd-foot-l">${esc(label)}</div><div class="srd-foot-v srd-fg-${tone}">${esc(value)}</div>
           ${note ? `<div class="srd-foot-n">${esc(note)}</div>` : ""}</div></div>`;
    return `<div class="srd-footstrip">
      ${cell("▲", "blue", `Predicted ${targetName} Score`.replace(/\s+/g, " ").trim(), predicted || "—", snap.predicted_label ? String(snap.predicted_label) : "model estimate")}
      ${cell("✪", "violet", `Predicted ${targetName} Rank`.replace(/\s+/g, " ").trim(), air ? `~${air}` : "—", "model estimate, not a result")}
      ${cell("◈", risk && /low/i.test(risk) ? "green" : risk && /med/i.test(risk) ? "amber" : "red", "Risk Level", risk || "—", sum.level ? `overall ${sum.level.toLowerCase()}` : "")}
      <div class="srd-sign">
        <div class="srd-sign-line"></div>
        <div class="srd-sign-l">Academic Head</div>
      </div>
      <div class="srd-gen">
        <div class="srd-foot-l">Report Generated On</div>
        <div class="srd-gen-v">${esc(moment().format("DD MMM YYYY"))}</div>
      </div>
    </div>
    <div class="srd-fineprint">
      ${esc(school)} · Overall Performance Report for ${esc(studentName(student))}. Every figure is cumulative over the student's full
      examination history; ranks and section averages are as reported per exam. Predicted score and rank are model estimates, not results.
    </div>`;
};

// The whole document. `data` comes from fetchStudentReport.
export const buildStudentReportHTML = ({ student, data, examStreamLabel }) => {
    const org = (data && data.org) || {};
    const school = org.organisation_name || cookieInstitutionName();
    const logo = normalizeLogoSrc(org.logo);
    const sum = summariseReport({
        examRows: (data && data.examRows) || [],
        subjectRows: (data && data.subjectRows) || [],
        subjectTrend: (data && data.subjectTrend) || [],
        speed: (data && data.speed) || [],
        topics: (data && data.topics) || [],
        matrix: (data && data.matrix) || [],
        metrics: data && data.metrics,
        attendance: data && data.attendance,
        streams: (data && data.streams) || [],
        snapshot: data && data.snapshot,
    });

    return `
    <div class="srd-doc">
      ${headerBand(school, logo, examStreamLabel, sum)}
      ${studentCard(student, school, sum)}
      ${kpiStrip(sum)}
      <div class="srd-grid srd-grid-7-5">
        ${scoreTrendPanel(sum)}
        ${subjectPanel(sum)}
      </div>
      <div class="srd-grid srd-grid-3">
        ${questionPanel(sum)}
        ${attendancePanel(sum)}
        ${rankPanel(sum)}
      </div>
      <div class="srd-grid srd-grid-2">
        ${summaryPanel(sum)}
        ${insightsPanel(sum)}
      </div>
      ${examTablePanel(sum)}
      ${footerStrip(sum, school, student)}
    </div>`;
};

/* ---------------------------------------------------------------------- css */

// Every selector is .srd-prefixed so the stylesheet can be injected anywhere without
// leaking into the surrounding UI.
export const STUDENT_REPORT_CSS = `
.srd-doc{background:#f4f7fb;color:#0f172a;font-family:Arial,Helvetica,sans-serif;font-size:8px;line-height:1.3;padding:6px}
.srd-doc *{box-sizing:border-box}
.srd-doc b{font-weight:800}

.srd-band{display:flex;align-items:center;justify-content:space-between;gap:10px;background:linear-gradient(90deg,#12306e 0%,#1d4ed8 100%);
          color:#fff;border-radius:6px;padding:7px 12px;margin-bottom:6px}
.srd-band-t{font-size:14px;font-weight:800;text-transform:uppercase;letter-spacing:.6px;line-height:1.15}
.srd-band-s{font-size:8px;font-weight:700;color:#fde047;text-transform:uppercase;letter-spacing:.5px;margin-top:1px}
.srd-band-r{display:flex;align-items:center;gap:7px;text-align:right;flex-shrink:0}
.srd-band-logo{width:26px;height:26px;object-fit:contain;background:#fff;border-radius:4px;padding:2px}
.srd-band-cap{font-size:16px}
.srd-band-org{font-size:9px;font-weight:800;text-transform:uppercase;letter-spacing:.4px}
.srd-band-prog{font-size:7.5px;color:#bfdbfe;font-weight:700;text-transform:uppercase}

.srd-card{display:grid;grid-template-columns:2.1fr 1fr 1fr 1fr;gap:8px;background:#fff;border:1px solid #e2e8f0;border-radius:6px;
          padding:8px;margin-bottom:6px}
.srd-card-id{display:flex;gap:9px;align-items:flex-start;border-right:1px solid #eef2f7;padding-right:8px}
.srd-photo{width:52px;height:52px;border-radius:50%;object-fit:cover;border:2px solid #dbeafe;flex-shrink:0}
.srd-photo-ph{background:#1d4ed8;color:#fff;font-size:22px;font-weight:800;display:flex;align-items:center;justify-content:center}
.srd-id-body{min-width:0}
.srd-name{font-size:12px;font-weight:800;color:#12306e;margin-bottom:2px}
.srd-id-line{display:flex;gap:3px;align-items:baseline;font-size:7.5px;line-height:1.5}
.srd-id-g{color:#2563eb;width:8px;flex-shrink:0}
.srd-id-k{color:#64748b;font-weight:700;width:52px;flex-shrink:0}
.srd-id-c{color:#cbd5e1}
.srd-id-v{font-weight:700;color:#0f172a;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.srd-card-cell{text-align:center;border-right:1px solid #eef2f7;padding:0 4px;display:flex;flex-direction:column;justify-content:center}
.srd-card-cell:last-child{border-right:none}
.srd-cell-g{font-size:10px;color:#2563eb}
.srd-cell-l{font-size:7.5px;color:#475569;font-weight:700;text-transform:uppercase;letter-spacing:.3px}
.srd-cell-v{font-size:17px;font-weight:800;color:#16a34a;line-height:1.1}
.srd-cell-sub{font-size:7px;color:#64748b;font-weight:700;margin-top:2px}
.srd-cell-sub2{font-size:7px;color:#64748b;margin-top:1px}
.srd-cell-grade{background:#fffbeb;border-radius:5px}
.srd-grade{display:flex;align-items:center;justify-content:center;gap:3px}
.srd-grade-v{font-size:24px;font-weight:800;color:#16a34a;line-height:1.05}
.srd-laurel{font-size:15px;color:#f59e0b}
.srd-gauge-svg{width:100%;max-width:120px;height:auto;margin:0 auto;display:block}

.srd-kpis{display:grid;grid-template-columns:repeat(8,1fr);gap:5px;margin-bottom:6px}
.srd-kpi{background:#fff;border:1px solid #e2e8f0;border-radius:6px;padding:5px 3px;text-align:center;page-break-inside:avoid}
.srd-kpi-ic{width:15px;height:15px;border-radius:4px;margin:0 auto 2px;display:flex;align-items:center;justify-content:center;font-size:9px}
.srd-ic-blue{background:#dbeafe;color:#1d4ed8}
.srd-ic-green{background:#dcfce7;color:#15803d}
.srd-ic-violet{background:#ede9fe;color:#6d28d9}
.srd-ic-sky{background:#e0f2fe;color:#0369a1}
.srd-ic-orange{background:#ffedd5;color:#c2410c}
.srd-ic-red{background:#fee2e2;color:#b91c1c}
.srd-ic-amber{background:#fef3c7;color:#b45309}
.srd-kpi-l{font-size:6.5px;color:#475569;font-weight:700;text-transform:uppercase;letter-spacing:.2px;line-height:1.25}
.srd-kpi-v{font-size:12px;font-weight:800;line-height:1.15;margin-top:1px}
.srd-kpi-u{font-size:7px;font-weight:700;color:#94a3b8}
.srd-kpi-n{font-size:6px;color:#94a3b8;margin-top:1px;line-height:1.2}
.srd-fg-blue{color:#1d4ed8}.srd-fg-green{color:#16a34a}.srd-fg-violet{color:#7c3aed}
.srd-fg-sky{color:#0284c7}.srd-fg-orange{color:#ea580c}.srd-fg-red{color:#dc2626}.srd-fg-amber{color:#d97706}

.srd-grid{display:grid;gap:6px;margin-bottom:6px}
.srd-grid-7-5{grid-template-columns:1.35fr 1fr}
.srd-grid-3{grid-template-columns:1fr 0.85fr 1.4fr}
.srd-grid-2{grid-template-columns:1fr 1.35fr}
.srd-panel{background:#fff;border:1px solid #e2e8f0;border-radius:6px;overflow:hidden;page-break-inside:avoid}
.srd-panel-hd{background:#12306e;color:#fff;padding:3.5px 8px;display:flex;align-items:baseline;gap:5px}
.srd-panel-t{font-size:8px;font-weight:800;text-transform:uppercase;letter-spacing:.5px}
.srd-panel-s{font-size:6.5px;color:#bfdbfe;font-weight:600}
.srd-panel-bd{padding:6px 8px}

.srd-line-svg,.srd-donut-svg{width:100%;height:auto;display:block}
.srd-chip{margin-top:4px;text-align:center;font-size:7.5px;font-weight:700;border-radius:4px;padding:3px 5px;line-height:1.3}
.srd-chip-green{background:#f0fdf4;color:#15803d;border:1px solid #bbf7d0}
.srd-chip-red{background:#fef2f2;color:#b91c1c;border:1px solid #fecaca}
.srd-chip-blue{background:#eff6ff;color:#1d4ed8;border:1px solid #bfdbfe}
.srd-chip-grey{background:#f8fafc;color:#64748b;border:1px solid #e2e8f0}

.srd-tbl{width:100%;border-collapse:collapse}
.srd-tbl th{font-size:6.5px;text-transform:uppercase;letter-spacing:.2px;color:#1d4ed8;background:#f1f5fd;
            border-bottom:1.2px solid #dbeafe;padding:3px;text-align:center;font-weight:800}
.srd-tbl td{font-size:7.5px;padding:3px;border-bottom:1px solid #f1f5f9;text-align:center;vertical-align:middle}
.srd-tbl thead{display:table-header-group}
.srd-tbl tr{page-break-inside:avoid}
.srd-tot td{background:#f8fafc;font-weight:800;border-top:1.2px solid #dbeafe;border-bottom:none}
.srd-l{text-align:left!important}
.srd-c{text-align:center!important;color:#94a3b8}
.srd-b{font-weight:800}
.srd-subj{font-weight:700}
.srd-mut{color:#94a3b8;font-weight:500}
.srd-empty{text-align:center!important;color:#94a3b8;padding:8px 4px!important;font-style:italic}
.srd-nodata{font-size:7.5px;color:#94a3b8;font-style:italic;padding:4px 0;line-height:1.4}
.srd-note{font-size:6.5px;color:#94a3b8;line-height:1.35;margin-top:3px}
.srd-pill{display:inline-block;padding:1px 5px;border-radius:8px;font-size:6.5px;font-weight:800;text-transform:uppercase;letter-spacing:.2px}
.srd-pill-strong,.srd-pill-excellent{background:#dcfce7;color:#15803d}
.srd-pill-good{background:#e0f2fe;color:#0369a1}
.srd-pill-average{background:#fef3c7;color:#b45309}
.srd-pill-needs-work,.srd-pill-needs-improvement{background:#fee2e2;color:#b91c1c}

.srd-donut-wrap{display:flex;align-items:center;gap:7px}
.srd-donut-svg{width:76px;flex-shrink:0}
.srd-donut-legend{flex:1;min-width:0}
.srd-lgi{display:flex;align-items:baseline;gap:4px;font-size:7.5px;color:#475569;line-height:1.65}
.srd-lgi b{margin-left:auto;color:#0f172a}
.srd-lgi-tot{border-top:1px solid #eef2f7;margin-top:2px;padding-top:2px;font-weight:700;color:#334155}
.srd-dot{width:9px;text-align:center;font-size:7px;font-weight:800}
.srd-dot-green{color:#16a34a}.srd-dot-red{color:#dc2626}.srd-dot-amber{color:#d97706}

.srd-stat{display:flex;align-items:baseline;gap:5px;font-size:7.5px;padding:2.5px 0;border-bottom:1px solid #f5f7fa}
.srd-stat:last-child{border-bottom:none}
.srd-stat-g{width:9px;text-align:center;flex-shrink:0;font-size:8px}
.srd-stat-l{color:#475569;font-weight:600}
.srd-stat-v{margin-left:auto;font-weight:800;text-align:right;white-space:nowrap}

.srd-rank-top{display:flex;justify-content:flex-end}
.srd-badge{display:flex;flex-direction:column;align-items:flex-end;border-radius:4px;padding:2px 6px;line-height:1.25}
.srd-badge-green{background:#f0fdf4;border:1px solid #bbf7d0}
.srd-badge-red{background:#fef2f2;border:1px solid #fecaca}
.srd-badge-grey{background:#f8fafc;border:1px solid #e2e8f0}
.srd-badge-t{font-size:6px;color:#475569;font-weight:700;text-transform:uppercase}
.srd-badge-v{font-size:9px;font-weight:800;color:#15803d}
.srd-badge-red .srd-badge-v{color:#b91c1c}
.srd-badge-grey .srd-badge-v{color:#64748b}

.srd-att-hero{text-align:center;margin-bottom:3px}
.srd-att-v{font-size:19px;font-weight:800;line-height:1.1}
.srd-att-n{font-size:6.5px;color:#64748b;font-weight:700;text-transform:uppercase;letter-spacing:.2px}
.srd-split{display:flex;height:7px;border-radius:4px;overflow:hidden;background:#fee2e2;margin-bottom:2px}
.srd-split-p{background:#22c55e}
.srd-split-a{flex:1;background:#ef4444}
.srd-split-lg{display:flex;justify-content:space-between;font-size:6.5px;font-weight:700;margin-bottom:3px}

.srd-tag{display:inline-block;font-size:6.5px;font-weight:800;text-transform:uppercase;letter-spacing:.3px;border-radius:3px;
         padding:1.5px 5px;margin:2px 0 2px}
.srd-tag-green{background:#dcfce7;color:#15803d}
.srd-tag-amber{background:#ffedd5;color:#c2410c}
.srd-tag-blue{background:#dbeafe;color:#1d4ed8}
.srd-ins-list{margin:0 0 2px;padding-left:11px}
.srd-ins-list li{font-size:7px;line-height:1.4;margin-bottom:1.5px}
.srd-rec{font-size:7px;line-height:1.4;color:#1d4ed8;font-weight:700}

.srd-footstrip{display:grid;grid-template-columns:1fr 1fr 1fr 0.9fr 0.9fr;gap:6px;background:#fff;border:1px solid #e2e8f0;
               border-radius:6px;padding:6px 8px;align-items:center}
.srd-foot-cell{display:flex;gap:5px;align-items:center}
.srd-foot-g{font-size:12px;flex-shrink:0}
.srd-foot-l{font-size:6.5px;color:#475569;font-weight:700;text-transform:uppercase;letter-spacing:.2px}
.srd-foot-v{font-size:11px;font-weight:800;line-height:1.15}
.srd-foot-n{font-size:6px;color:#94a3b8}
.srd-sign{text-align:center}
.srd-sign-line{border-bottom:1px solid #94a3b8;height:14px}
.srd-sign-l{font-size:6.5px;color:#475569;font-weight:700;margin-top:2px}
.srd-gen{text-align:right}
.srd-gen-v{font-size:8.5px;font-weight:800;color:#12306e}
.srd-fineprint{font-size:6px;color:#94a3b8;line-height:1.4;margin-top:4px;padding:0 2px}
`;

// Page setup — print only, kept out of STUDENT_REPORT_CSS so injecting the stylesheet
// elsewhere never touches the host page's own body styles.
const STUDENT_REPORT_PRINT_CSS = `
@page{size:A4 portrait;margin:6mm}
*{-webkit-print-color-adjust:exact!important;print-color-adjust:exact!important}
html,body{margin:0;padding:0;background:#fff}
`;

/* -------------------------------------------------------------------- print */

// A4 portrait at 96dpi, less the 6mm print margins declared above.
const PAGE_W_PX = ((210 - 12) / 25.4) * 96;
const PAGE_H_PX = ((297 - 12) / 25.4) * 96;
// Below this the type stops being readable — a very long exam history is allowed to run
// onto a second page rather than shrink into illegibility.
const MIN_SCALE = 0.55;

// Prints through a hidden iframe (the browser's "Save as PDF" destination is the download
// path). Waits for images first — print() does not wait for them — then scales the document
// so the dashboard lands on ONE page.
export const printStudentReportHTML = (title, bodyHTML) => {
    const iframe = document.createElement("iframe");
    iframe.setAttribute("aria-hidden", "true");
    // Real A4 width offscreen: measuring inside a 0-width frame reports a bogus height.
    iframe.style.cssText = `position:fixed;left:-10000px;top:0;width:${Math.round(PAGE_W_PX)}px;height:${Math.round(PAGE_H_PX)}px;border:0;`;
    document.body.appendChild(iframe);
    const cleanup = () =>
        setTimeout(() => {
            try {
                document.body.removeChild(iframe);
            } catch (e) { }
        }, 1000);

    const doc = iframe.contentWindow.document;
    doc.open();
    doc.write(
        `<!DOCTYPE html><html><head><title>${esc(title)}</title>` +
        `<style>${STUDENT_REPORT_CSS}${STUDENT_REPORT_PRINT_CSS}</style>` +
        `</head><body>${bodyHTML}</body></html>`
    );
    doc.close();

    const win = iframe.contentWindow;
    const imgs = Array.from(doc.images || []);
    Promise.all(
        imgs.map((img) =>
            img.complete
                ? Promise.resolve()
                : new Promise((res) => {
                    img.onload = res;
                    img.onerror = res;
                })
        )
    ).then(() =>
        setTimeout(() => {
            try {
                const root = doc.querySelector(".srd-doc");
                if (root) {
                    const h = root.getBoundingClientRect().height;
                    if (h > PAGE_H_PX) {
                        // zoom (not transform) so the printed page box shrinks with the
                        // content — a transform scales the paint but keeps the layout
                        // height, and would still spill onto a second sheet.
                        root.style.zoom = Math.max(MIN_SCALE, PAGE_H_PX / h);
                    }
                }
            } catch (e) { }
            try {
                win.focus();
                win.print();
            } catch (e) { }
            cleanup();
        }, 300)
    );
};

// Suggested PDF file name — most browsers seed "Save as PDF" from the document title.
export const reportFileTitle = (student) =>
    `Overall Performance Report - ${studentName(student)} - ${moment().format("DD MMM YYYY")}`;
